import {
  getCellValueFromActiveWorksheet,
  getSelectedCell,
  getSheetName,
  getWorksheetId,
} from "@/jspreadsheet/worksheet";

import {
  getAutoTagStatus,
  getAutoTagToolCalls,
  getCellAutoTagInfo,
  getCellsList,
  getMappingGuideWithRecommendedTags,
  getPeriodFromCell,
  getTableInfo,
  getTableNotUsedSumTagKeys,
  getTagsFromCell,
  searchFromCell,
} from "@/apis/table/route";
import {
  getCategories,
  getCellOfRole,
  getDsdView,
  getDsdViewsByNoteNumbers,
  getRegisteredTableList,
  getTableAddresses,
  getTablesTaggingStatus,
  getWorkbook,
} from "@/apis/workbook/route";
import {
  getAllPresentationRoles,
  getDefinitionRolesByPresentation,
} from "@/apis/category/route";
import {
  getForwardStatus,
  getRollForwardInfo,
  getVersionForwardChanges,
} from "@/apis/forward/route";
import { getTaggingGuidance } from "@/apis/tagging/route";
import { getNextDisclosureDeadline } from "@/apis/disclosure-deadline/route";

import { getTableStructure } from "@/apis/ai/route";
import {
  getChunkDetail,
  getComparisonHistory,
  getComparisonSummary,
} from "@/apis/compare/route";
import { getDsdFiles as getDsdDsdCompareFiles } from "@/apis/dsd-dsd-compare/route";
import {
  getDsdFiles,
  getTaskStatus,
  getVersionForwardReview,
} from "@/apis/dsd/route";
import {
  getDsdEditorReport,
  getDsdEditorReports,
  getDsdEditorSpellcheck,
} from "@/apis/dsd-editor/route";
import {
  getEditablePreview,
  getEditableTreeView,
} from "@/apis/editable-preview/route";
import {
  getDartFilingPreview,
  getIxdNoteChecklist,
  getIxdPreview,
  getIxdReports,
} from "@/apis/ixd-editor/route";
import {
  getIxdMatchPreview,
  getIxdValueSyncPlan,
} from "@/apis/ixd-value-sync/route";

import { getWorksheets } from "@/apis/jspreadsheet/route";
import { getTableTreeViewV2 } from "@/apis/preview/v2/route";
import getTreeViewUsedInRole, {
  getMappingGuideWithMappedTags,
} from "@/apis/role/route";
import { CellInfo } from "@/apis/table/types";
import { getTagExists } from "@/apis/tag/route";
import {
  getAxisTagsInRole,
  getRoleInfo,
  getRoleList,
  getTagDetail,
  getTagUsageInfo,
  postSimilarTags,
} from "@/apis/taxonomy/route";
import { getSources } from "@/apis/cs-rag/route";
import {
  getReferenceTable,
  getReferenceTableXbrlMapping,
} from "@/apis/reference-search/route";
import type { ReferenceTableId } from "@/apis/reference-search/types";
import { postUserNames } from "@/apis/user/route";
import { getActiveWorksheetName } from "@/jspreadsheet/jspreadsheet";
import { Dimension } from "@/types/mapping";
import { createQueryKeys } from "@lukemorales/query-key-factory";

const queries = createQueryKeys("server", {
  tags: () => ({
    queryKey: ["tags"],
    contextQueries: {
      exists: (
        workbookId: string,
        koLabel: string,
        enLabel: string,
        dimension: Dimension,
      ) => ({
        queryKey: [workbookId, koLabel, enLabel, dimension],
        queryFn: () => {
          return getTagExists(workbookId, koLabel, enLabel, dimension);
        },
      }),
    },
  }),
  workbook: (workbookId: string) => ({
    queryKey: ["workbooks", workbookId],
    queryFn: () => getWorkbook(workbookId),
    contextQueries: {
      tableAddresses: () => ({
        queryKey: ["table-addresses"],
        queryFn: () => getTableAddresses(workbookId),
      }),
      registeredTables: () => ({
        queryKey: ["tables"],
        queryFn: () => getRegisteredTableList(workbookId),
      }),
      tags: () => ({
        queryKey: ["tags"],
        contextQueries: {
          similarTags: (koLabel: string, dimension: Dimension) => ({
            queryKey: [koLabel, dimension],
            queryFn: () => {
              return postSimilarTags(workbookId, koLabel, dimension);
            },
          }),
        },
      }),
      categories: () => ({
        queryKey: ["categories"],
        queryFn: () => {
          return getCategories(workbookId);
        },
      }),
      ixdMatchPreview: (reportId: string) => ({
        queryKey: ["ixd-match-preview", reportId],
        queryFn: () => getIxdMatchPreview(workbookId, reportId),
        // 전역 retry:false를 덮어써 IXD Editor 프리뷰와 동일하게
        // 일시적인 네트워크·응답 오류를 최대 3회 재시도한다.
        retry: 3,
      }),
      /**
       * 동기화 실행 전 "무엇이 반영되는지" 확인용. 쓰기가 없다.
       * plan과 POST 사이에 상태가 바뀔 수 있으므로 확인 화면을 오래 띄우는
       * 흐름에서는 실행 직전 refetch 한다.
       */
      ixdValueSyncPlan: (reportId: string) => ({
        queryKey: ["ixd-value-sync-plan", reportId],
        queryFn: () => getIxdValueSyncPlan(workbookId, reportId),
      }),
      dsdViewsByNoteNumbers: (noteNumbers: string[]) => ({
        queryKey: ["dsd-views-by-note-numbers", [...noteNumbers].sort()],
        queryFn: () => getDsdViewsByNoteNumbers(workbookId, noteNumbers),
      }),
      presentationRoles: () => ({
        queryKey: ["presentation-roles"],
        queryFn: () => getAllPresentationRoles(workbookId),
      }),
      definitionRoles: (preKey: string) => ({
        queryKey: ["definition-roles", preKey],
        queryFn: () => getDefinitionRolesByPresentation(workbookId, preKey),
      }),
      taskStatus: () => ({
        queryKey: ["task-status"],
        queryFn: () => getTaskStatus(workbookId),
      }),
      autoTagStatus: () => ({
        queryKey: ["auto-tag-status"],
        queryFn: () => getAutoTagStatus(workbookId),
      }),
      forwardStatus: () => ({
        queryKey: ["forward-status"],
        queryFn: () => getForwardStatus(workbookId),
      }),
      taggingGuidance: () => ({
        queryKey: ["tagging-guidance"],
        queryFn: () => getTaggingGuidance(workbookId),
      }),
      rollForwardInfo: () => ({
        queryKey: ["roll-forward-info"],
        queryFn: () => getRollForwardInfo(workbookId),
      }),
      versionForwardChanges: () => ({
        queryKey: ["version-forward-changes"],
        queryFn: () => getVersionForwardChanges(workbookId),
      }),
      autoTagToolCalls: (tableId: string) => ({
        queryKey: ["auto-tag-tool-calls", tableId],
        queryFn: () => getAutoTagToolCalls(workbookId, tableId),
      }),
      tablesTaggingStatus: () => ({
        queryKey: ["tables-tagging-status", workbookId],
        queryFn: () => {
          return getTablesTaggingStatus(workbookId);
        },
      }),
      tables: (tableId: string) => ({
        queryKey: [tableId],
        queryFn: () => {
          return getTableInfo(workbookId, tableId);
        },
        contextQueries: {
          preview: () => ({
            queryKey: ["preview"],
            queryFn: () => {
              return {
                queryKey: [
                  "workbooks",
                  workbookId,
                  "tables",
                  tableId,
                  "preview",
                ],
                workbookId: workbookId,
                tableId: tableId,
              };
            },
          }),
          dsdView: () => ({
            queryKey: ["dsd-view"],
            queryFn: () => {
              return getDsdView(workbookId, tableId);
            },
          }),
          editablePreview: () => ({
            queryKey: ["editable-preview"],
            contextQueries: {
              withQueryParam: (notIsSumMemberTags: string[]) => ({
                queryKey: [notIsSumMemberTags],
                queryFn: () => {
                  return getEditablePreview(
                    workbookId,
                    tableId,
                    notIsSumMemberTags,
                  );
                },
              }),
            },
          }),
          tableTreeViewV2: () => ({
            queryKey: ["table-tree-view-v2"],
            queryFn: () => {
              return getTableTreeViewV2(workbookId, tableId);
            },
          }),
          editableTreeView: () => ({
            queryKey: ["editable-tree-view"],
            queryFn: () => {
              return getEditableTreeView(workbookId, tableId);
            },
          }),

          taxonomy: () => ({
            queryKey: ["taxonomy"],
            contextQueries: {
              tags: (dimension: Dimension, cellAddress: string) => ({
                queryKey: [dimension, cellAddress],
                queryFn: () => {
                  return searchFromCell(
                    workbookId,
                    tableId,
                    dimension,
                    cellAddress,
                  );
                },
              }),
            },
          }),
          cells: (cellAddress?: string) => ({
            queryKey: [cellAddress],
            queryFn: () => {
              return getCellsList(workbookId, tableId);
            },
            contextQueries: {
              role: () => ({
                queryKey: ["role"],
                queryFn: () => {
                  return getCellOfRole(workbookId, tableId, cellAddress);
                },
              }),
              autoTagInfo: () => ({
                queryKey: ["auto-tag-info"],
                queryFn: () =>
                  getCellAutoTagInfo(workbookId, tableId, cellAddress),
              }),
              tags: (tagKey?: string) => ({
                queryKey: ["tagged"],
                queryFn: () =>
                  getTagsFromCell(workbookId, tableId, cellAddress),
                contextQueries: {
                  mappingGuide: () => ({
                    queryKey: [tagKey],
                    queryFn: () => {
                      return getMappingGuideWithRecommendedTags(
                        workbookId,
                        tableId,
                        tagKey!,
                        cellAddress!,
                      );
                    },
                  }),
                },
              }),
              period: () => ({
                queryKey: ["period"],
                queryFn: () => {
                  return getPeriodFromCell(workbookId, tableId, cellAddress);
                },
              }),
            },
          }),
          notUsedSumTagKeys: () => ({
            queryKey: ["not-used-sum-tag-keys"],
            queryFn: () => {
              return getTableNotUsedSumTagKeys(workbookId, tableId);
            },
          }),
        },
      }),
      taxonomy: () => ({
        queryKey: ["taxonomy"],
        contextQueries: {
          roles: (roleKey?: string, usedOnly?: boolean) => ({
            queryKey: ["roles", roleKey, usedOnly],
            queryFn: () => {
              return getRoleList(workbookId, usedOnly);
            },
            contextQueries: {
              axes: (tableId: string) => ({
                queryKey: [tableId],
                queryFn: () => {
                  return getAxisTagsInRole(workbookId, roleKey!, tableId);
                },
              }),
              usedTags: (tableId: string) => ({
                queryKey: [roleKey, tableId],
                contextQueries: {
                  treeView: () => ({
                    queryKey: ["tree-view"],
                    queryFn: () => {
                      return getTreeViewUsedInRole(
                        workbookId,
                        roleKey!,
                        tableId!,
                      );
                    },
                  }),
                },
              }),
              mappingGuide: (focusTagKey: string) => ({
                queryKey: [focusTagKey],
                queryFn: () => {
                  return getMappingGuideWithMappedTags(
                    workbookId,
                    roleKey!,
                    focusTagKey,
                  );
                },
              }),
              roleInfo: (roleKey: string) => ({
                queryKey: [roleKey],
                queryFn: () => {
                  return getRoleInfo(workbookId, roleKey);
                },
              }),
            },
          }),
          tags: (
            tagKey?: string,
            tableId?: string,

            roleKey?: string,
          ) => ({
            queryKey: [tableId, tagKey, roleKey],
            queryFn: () => getTagDetail(workbookId, tableId, tagKey, roleKey),
            contextQueries: {
              usageInfo: () => ({
                queryKey: [tagKey],
                queryFn: () => {
                  if (!tagKey) {
                    return null;
                  }
                  return getTagUsageInfo(workbookId, tagKey);
                },
              }),
            },
          }),
          usedRoles: (usedOnly: boolean = false) => ({
            queryKey: ["used-roles", usedOnly],
            queryFn: () => {
              return getRoleList(workbookId, true);
            },
          }),
        },
      }),
    },
  }),
  ai: {
    queryKey: ["ai"],
    queryFn: () => {
      return null;
    },
    contextQueries: {
      tableStructure: (cellInfos: CellInfo[]) => ({
        queryKey: [cellInfos],
        queryFn: () => {
          return getTableStructure(cellInfos);
        },
      }),
    },
  },
  comparison: (comparisonId: string) => ({
    queryKey: ["comparison", comparisonId],
    queryFn: () => getComparisonSummary(comparisonId),
    contextQueries: {
      chunk: (chunkId: string) => ({
        queryKey: ["chunk", chunkId],
        queryFn: ({ signal }) => getChunkDetail(comparisonId, chunkId, signal),
      }),
    },
  }),
  comparisonHistory: (limit: number, offset: number) => ({
    queryKey: ["comparison-history", limit, offset],
    queryFn: () => getComparisonHistory(limit, offset),
  }),
  ixdEditorReports: () => ({
    queryKey: ["ixd-editor-reports"],
    queryFn: () => getIxdReports(),
  }),
  ixdEditor: (reportId: string) => ({
    queryKey: ["ixd-editor", reportId],
    contextQueries: {
      preview: () => ({
        queryKey: ["preview"],
        queryFn: () => getIxdPreview(reportId),
        // 프리뷰는 단일 JSON을 chunked 스트림으로 받는다. 스트림이 가끔 중간에
        // 끊겨 불완전 응답이 오면(getIxdPreview가 throw) 재시도해 결국 성공하게 한다.
        // 전역 기본값은 retry:false 라 이 쿼리에만 켠다. ixd-editor·ixd-dsd-compare
        // 편집 경로가 이 정의를 공유하므로 여기 한 곳에서 상속시킨다.
        retry: 3,
        // 리포트 프리뷰는 저장 후 명시적으로 재조회하기 전까지 불변이다. 큰 파일에서
        // 포커스 복귀마다 자동 refetch가 일어나면, 거대 응답을 이전 캐시와
        // replaceEqualDeep(structural sharing)로 트리 전체 비교하다 V8 프로퍼티 열거
        // 한계("Too many properties to enumerate")에 걸린다. 자동 refetch를 끄고
        // structural sharing도 꺼서 그 walk 자체를 없앤다.
        staleTime: Infinity,
        structuralSharing: false,
      }),
      noteChecklist: () => ({
        queryKey: ["note-checklist"],
        queryFn: () => getIxdNoteChecklist(reportId),
        // 페이지 진입 때 한 번만 계산한다. 편집 후 결과는 다시 들어와야 바뀌므로
        // 포커스 refetch 는 끄고, 떠날 때 캐시를 버려 재진입 시 새로 계산하게 한다.
        staleTime: Infinity,
        gcTime: 0,
      }),
    },
  }),
  /** DART 공시 XBRL(zip) 열람본. 편집이 없어 preview 하나뿐이다. */
  dartFilingView: (filingId: string) => ({
    queryKey: ["dart-filing-view", filingId],
    contextQueries: {
      preview: () => ({
        queryKey: ["preview"],
        queryFn: () => getDartFilingPreview(filingId),
        // 옵션 근거는 ixdEditor.preview 와 같다 — chunked 스트림 절단 재시도와,
        // 거대 응답의 structural sharing walk 회피.
        retry: 3,
        staleTime: Infinity,
        structuralSharing: false,
      }),
    },
  }),
  dsdEditorReports: ({ limit, offset }: { limit: number; offset: number }) => ({
    queryKey: ["dsd-editor-reports", limit, offset],
    queryFn: () => getDsdEditorReports({ limit, offset }),
  }),
  versionForwardReview: (srcWorkbookId: string, trgWorkbookId: string) => ({
    queryKey: ["version-forward-review", srcWorkbookId, trgWorkbookId],
    queryFn: () => getVersionForwardReview(srcWorkbookId, trgWorkbookId),
  }),
  dsdEditor: (documentId: string) => ({
    queryKey: ["dsd-editor", documentId],
    contextQueries: {
      report: (unitDifferenceV2 = false) => ({
        queryKey: ["report", { unitDifferenceV2 }],
        queryFn: () => getDsdEditorReport(documentId, unitDifferenceV2),
      }),
      spellcheck: () => ({
        queryKey: ["spellcheck"],
        queryFn: () => getDsdEditorSpellcheck(documentId),
      }),
    },
  }),
  dsd: () => ({
    queryKey: ["dsd"],
    queryFn: () => {
      return getDsdFiles();
    },
  }),
  dsdDsdCompare: () => ({
    queryKey: ["dsd-dsd-compare"],
    contextQueries: {
      files: () => ({
        queryKey: ["files"],
        queryFn: () => getDsdDsdCompareFiles(),
      }),
      history: (limit: number) => ({
        queryKey: ["history", limit],
      }),
    },
  }),
  userNames: (emails: string[]) => ({
    queryKey: ["user-names", emails],
    queryFn: () => postUserNames(emails),
  }),
  nextDisclosureDeadline: () => ({
    queryKey: ["next-disclosure-deadline"],
    queryFn: getNextDisclosureDeadline,
  }),
});

const excelQueries = createQueryKeys("excel", {
  sheetId: (sheetName?: string) => ({
    queryKey: [sheetName],
    queryFn: () => getWorksheetId(),
  }),
  cellValue: (tableId: string, address: string) => ({
    queryKey: ["current", tableId, address],
    queryFn: () => getCellValueFromActiveWorksheet(address),
  }),
  currentCell: () => ({
    queryKey: ["current"],
    queryFn: getSelectedCell,
  }),
  activeWorksheet: () => ({
    queryKey: ["active"],
    queryFn: getActiveWorksheetName,
  }),
  sheetName: (sheetId: string) => ({
    queryKey: ["sheet-name", sheetId],
    queryFn: () => getSheetName(sheetId),
  }),
});

const ssQueries = createQueryKeys("ss", {
  spreadsheets: (workbookId: string) => ({
    queryKey: ["spreadsheets", workbookId],
    contextQueries: {
      category: () => ({
        queryKey: ["category"],
        queryFn: () => getWorksheets(workbookId),
      }),
    },
  }),
});

const csRagQueries = createQueryKeys("csRag", {
  sources: () => ({
    queryKey: ["sources"],
    queryFn: getSources,
  }),
});

const referenceSearchQueries = createQueryKeys("referenceSearch", {
  table: (id: ReferenceTableId) => ({
    queryKey: [id.rceptNo, id.attachmentCode, id.noteId, id.subIndex],
    queryFn: () => getReferenceTable(id),
  }),
  xbrlMapping: (id: ReferenceTableId) => ({
    queryKey: [
      "xbrl-mapping",
      id.rceptNo,
      id.attachmentCode,
      id.noteId,
      id.subIndex,
    ],
    queryFn: () => getReferenceTableXbrlMapping(id),
  }),
});

export default queries;

export { csRagQueries, excelQueries, referenceSearchQueries, ssQueries };
